import type { FC } from 'react';
import { useState, useCallback, useEffect } from 'react';
import User01Icon from '@untitled-ui/icons-react/build/esm/User01';
import { Avatar, Box, ButtonBase, SvgIcon } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMockedUser } from 'src/hooks/use-mocked-user';
import { usePopover } from 'src/hooks/use-popover';
import { useAuth } from 'src/hooks/use-auth';
import { supabase } from 'src/api/supabase/client';
import { AccountPopover } from './account-popover';

export const AccountButton: FC = () => {
  const user = useMockedUser();
  const { user: authUser } = useAuth();
  const isSalesman = authUser?.role === 'salesman';
  const popover = usePopover<HTMLButtonElement>();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const email = authUser?.email;
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!email) {
      setAvatarUrl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('user_profiles')
        .select('avatar_url')
        .eq('email', email)
        .maybeSingle();
      if (!cancelled) setAvatarUrl(data?.avatar_url ?? null);
    })();
    return () => { cancelled = true; };
  }, [email]);

  const handleAvatarChange = useCallback((url: string) => {
    setAvatarUrl(url);
  }, []);

  const avatarSrc = avatarUrl || (isSalesman ? undefined : '/assets/zach.png');

  return (
    <>
      <Box
        component={ButtonBase}
        onClick={popover.handleOpen}
        ref={popover.anchorRef}
        sx={{
          alignItems: 'center',
          display: 'flex',
          borderWidth: 2,
          borderStyle: 'solid',
          borderColor: isSalesman
            ? 'rgba(41, 112, 255, 0.3)'
            : (isDarkMode ? 'divider' : 'rgba(0, 0, 0, 0.12)'),
          height: 46,
          width: 46,
          borderRadius: '50%',
          bgcolor: isSalesman ? '#ffffff' : (isDarkMode ? 'transparent' : '#f5f5f5'),
        }}
      >
        <Avatar
          sx={{
            height: 38,
            width: 38,
            bgcolor: isSalesman ? '#ffffff' : (isDarkMode ? 'neutral.300' : 'primary.main'),
          }}
          src={avatarSrc}
        >
          <SvgIcon sx={isSalesman ? { color: '#2970FF', fontSize: 22 } : undefined}>
            <User01Icon />
          </SvgIcon>
        </Avatar>
      </Box>
      <AccountPopover
        anchorEl={popover.anchorRef.current}
        onClose={popover.handleClose}
        open={popover.open}
        onAvatarChange={handleAvatarChange}
      />
    </>
  );
};
